import { useMemo, useState } from 'react';
import {
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconPlus, IconSearch, IconTemplate } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { TemplateBrowser } from '@/components/template-browser';
import { TemplateVariableWizard } from '@/components/template-variable-wizard';
import { useOrg } from '@/hooks/use-org';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { isTemplatesEnabled } from '@/lib/features';
import { CreatePersonaMutation, PersonasListSubscription } from './-components/graphql';
import { readOutputHandles } from '@/components/output-handles';
import { NewPersonaCard, PersonaCard } from './-components/persona-cards';

export const Route = createFileRoute('/_authed/personas/')({
  component: PersonasPage,
});

function PersonasPage() {
  const navigate = useNavigate();
  const { organizationId, isLoading: isOrgLoading } = useOrg();

  const [{ data, error: subscriptionError }] = useSubscription({
    query: PersonasListSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const [, runCreatePersona] = useMutation(CreatePersonaMutation);

  const [search, setSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deletingPersonaId, setDeletingPersonaId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const personas = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (data?.personas ?? [])
      .filter((p) => !term || p.name.toLowerCase().includes(term) || p.description.toLowerCase().includes(term))
      .map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        documentCount: p.personaDocumentsAggregate.aggregate?.count ?? 0,
        outputCount: readOutputHandles(p.outputHandles).length,
      }));
  }, [data, search]);

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setNewName('');
    setNewDescription('');
    setCreateError(null);
  };

  const closeTemplateFlow = () => {
    setSelectedTemplateId(null);
    setTemplateBrowserOpen(false);
  };

  const handleCreate = async () => {
    if (!organizationId || !newName.trim()) return;

    setIsSubmitting(true);
    setCreateError(null);

    const result = await runCreatePersona({
      object: {
        organizationId,
        name: newName.trim(),
        description: newDescription.trim(),
        instructions: '',
        greeting: 'Hello, how can I help you today?',
        maxTurns: 10,
        outputHandles: [],
        ragNamespace: `persona_${organizationId}_${Date.now()}`,
      },
    });

    setIsSubmitting(false);

    if (result.error || !result.data?.insertPersonasOne) {
      setCreateError(getErrorMessage(result.error, 'Failed to create persona'));

      return;
    }

    closeCreateModal();
    void navigate({ to: '/personas/$personaId', params: { personaId: result.data.insertPersonasOne.id } });
  };

  const handleDelete = async () => {
    if (!deletingPersonaId) return;

    setIsSubmitting(true);
    setDeleteError(null);

    const result = await api.personas({ personaId: deletingPersonaId }).delete();

    setIsSubmitting(false);

    if (result.error) {
      setDeleteError(getErrorMessage(result.error, 'Failed to delete persona'));

      return;
    }

    setDeletingPersonaId(null);
  };

  if (isOrgLoading || (organizationId && data === undefined && !subscriptionError)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>AI Personas</Title>
              <Text c="dimmed" mt="xs">
                Configure AI agents for your IVR flows
              </Text>
            </div>
            <Group gap="xs">
              {isTemplatesEnabled() && (
                <Button
                  variant="light"
                  leftSection={<IconTemplate size={16} />}
                  onClick={() => setTemplateBrowserOpen(true)}
                >
                  From Template
                </Button>
              )}
              <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateModalOpen(true)}>
                New Persona
              </Button>
            </Group>
          </Group>

          <ErrorAlert
            message={subscriptionError ? getErrorMessage(subscriptionError, 'Failed to load personas') : null}
          />

          <TextInput
            placeholder="Search personas..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maw={360}
          />

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {personas.map((persona) => (
              <PersonaCard
                key={persona.id}
                id={persona.id}
                name={persona.name}
                description={persona.description}
                documentCount={persona.documentCount}
                outputCount={persona.outputCount}
                onDelete={() => setDeletingPersonaId(persona.id)}
              />
            ))}
            <NewPersonaCard onClick={() => setCreateModalOpen(true)} />
          </SimpleGrid>
        </Stack>
      </Container>

      <Modal opened={createModalOpen} onClose={closeCreateModal} title="Create New Persona">
        <Stack>
          <ErrorAlert message={createError} />
          <TextInput
            label="Name"
            placeholder="e.g. Customer Support Agent"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Brief description of what this persona does"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={isSubmitting} disabled={!newName.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={!!deletingPersonaId} onClose={() => setDeletingPersonaId(null)} title="Delete Persona">
        <Stack>
          <ErrorAlert message={deleteError} />
          <Text>
            Are you sure you want to delete this persona? This will also delete all associated knowledge base documents.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeletingPersonaId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={isSubmitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={templateBrowserOpen && !selectedTemplateId}
        onClose={() => setTemplateBrowserOpen(false)}
        title="Choose a Persona Template"
        size="lg"
      >
        <TemplateBrowser
          type="persona"
          onSelect={setSelectedTemplateId}
          onCancel={() => setTemplateBrowserOpen(false)}
        />
      </Modal>

      <Modal opened={!!selectedTemplateId} onClose={closeTemplateFlow} title="Configure Template" size="lg">
        {selectedTemplateId && (
          <TemplateVariableWizard
            templateId={selectedTemplateId}
            type="persona"
            organizationId={organizationId}
            onComplete={(newId) => {
              closeTemplateFlow();
              void navigate({ to: '/personas/$personaId', params: { personaId: newId } });
            }}
            onBack={() => setSelectedTemplateId(null)}
          />
        )}
      </Modal>
    </>
  );
}
